// AttemptWindow builder. Phase C.
//
// Pure function: take a flat ChildAttempt array + a (childId, skillId)
// scope, return the windowed view the rest of the engine consumes.
// Computes:
//   - relevantAttempts: matches childId + skillId
//   - lastRelevantAttempts: tail of N (default 3)
//   - independentAttempts: hintLevel ≤ H1, no audio/picture/adult support
//   - countedMasteryAttempts: passed canCountTowardMastery for primaryChannel
//   - countedReadinessAttempts: passed canCountTowardReadiness for primaryChannel
//   - transferAttempts, delayedReviewAttempts
//   - accuracy/independence rates, highHintRate
//   - h4Used, readAloudOnlyReadingEvidence, oralOnlyReadingEvidence
//   - sameErrorLoop: ≥2 of last 3 share a misconception
//   - dominantMisconceptionId, repeatedItemRisk, frustrationRate

import type {
  ChildAttempt,
  AttemptWindow,
  HintLevel,
  MasteryChannel,
} from "./types";
import { canCountTowardMastery, canCountTowardReadiness, isPostIdkSupported, readAloudIsSupportForChannel } from "./supportModeGating";
import { inferChannel } from "./masteryClassInference";

const HINT_ORDER: Record<HintLevel, number> = {
  H0: 0,
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
};

interface BuildAttemptWindowOptions {
  /** Tail size for `lastRelevantAttempts` and same-error-loop detection. Default 3. */
  lastRelevantCount?: number;
  /** Explicit primary channel override; defaults to inferChannel(skillId). */
  primaryChannel?: MasteryChannel;
}

export function buildAttemptWindow(
  allAttempts: ChildAttempt[],
  childId: string,
  skillId: string,
  options: BuildAttemptWindowOptions = {},
): AttemptWindow {
  const lastRelevantCount = options.lastRelevantCount ?? 3;
  const channel = options.primaryChannel ?? inferChannel(skillId);

  const relevantAttempts = allAttempts.filter(
    (a) => a.childId === childId && a.skillId === skillId,
  );
  const lastRelevantAttempts = relevantAttempts.slice(-lastRelevantCount);

  // Sort ascending by timestamp for window/start/end.
  const sorted = [...relevantAttempts].sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  );

  const isIndependent = (a: ChildAttempt) =>
    // MA-483 — a post-IDK answer is a HELPED answer whatever its hintLevel
    // says. (This particular `isIndependent` feeds window telemetry —
    // independentAttempts / independentRate — rather than a live gate; it is
    // corrected anyway so the window and the mastery scorer cannot disagree
    // about what "independent" means.)
    !isPostIdkSupported(a) &&
    HINT_ORDER[a.hintLevel] <= HINT_ORDER.H1 &&
    !a.readAloudUsed &&
    !a.pictureSupportUsed &&
    !a.adultSupportUsed &&
    !a.mascotSupportUsed;

  const independentAttempts = relevantAttempts.filter(isIndependent);
  const countedMasteryAttempts = relevantAttempts.filter((a) =>
    canCountTowardMastery(a, channel),
  );
  const countedReadinessAttempts = relevantAttempts.filter((a) =>
    canCountTowardReadiness(a, channel),
  );
  const transferAttempts = relevantAttempts.filter((a) => a.isTransferTask);
  const delayedReviewAttempts = relevantAttempts.filter((a) => a.isDelayedReview);

  const totalAttempts = relevantAttempts.length;
  const correctCount = relevantAttempts.filter((a) => a.correct).length;
  const accuracyRate = totalAttempts === 0 ? 0 : correctCount / totalAttempts;
  const independentRate = totalAttempts === 0 ? 0 : independentAttempts.length / totalAttempts;

  const highHintCount = relevantAttempts.filter(
    (a) => HINT_ORDER[a.hintLevel] >= HINT_ORDER.H2,
  ).length;
  const highHintRate = totalAttempts === 0 ? 0 : highHintCount / totalAttempts;
  const h4Used = relevantAttempts.some((a) => a.hintLevel === "H4");

  // Reading-evidence-only flags (spec lines 252-253). True when ALL
  // correct attempts in this window used the relevant support.
  const correctRelevant = relevantAttempts.filter((a) => a.correct);
  // ⭐MA-786: only on channels where read-aloud IS support (shared predicate).
  const readAloudOnlyReadingEvidence =
    readAloudIsSupportForChannel(channel) &&
    correctRelevant.length > 0 && correctRelevant.every((a) => a.readAloudUsed);
  const oralOnlyReadingEvidence =
    correctRelevant.length > 0 && correctRelevant.every((a) => a.responseChannel === "oral");

  // Same-error loop: ≥2 of last N attempts share a misconceptionId.
  let sameErrorLoop = false;
  let dominantMisconceptionId: string | null = null;
  if (lastRelevantAttempts.length >= 2) {
    const misCounts = new Map<string, number>();
    for (const a of lastRelevantAttempts) {
      if (a.misconceptionId) {
        misCounts.set(a.misconceptionId, (misCounts.get(a.misconceptionId) ?? 0) + 1);
      }
    }
    for (const [mid, count] of misCounts) {
      if (count >= 2) {
        sameErrorLoop = true;
        dominantMisconceptionId = mid;
        break;
      }
    }
  }

  // Repeated-item risk: same itemId appears >1 in the relevant window.
  const itemCounts = new Map<string, number>();
  for (const a of relevantAttempts) itemCounts.set(a.itemId, (itemCounts.get(a.itemId) ?? 0) + 1);
  const repeatedItemRisk = Array.from(itemCounts.values()).some((c) => c > 1);

  // Frustration rate: fraction of attempts with frustrationSignal "high".
  const highFrustrationCount = relevantAttempts.filter(
    (a) => a.frustrationSignal === "high",
  ).length;
  const frustrationRate = totalAttempts === 0 ? 0 : highFrustrationCount / totalAttempts;

  return {
    childId,
    skillId,
    domain: relevantAttempts[0]?.domain ?? "phonics_decoding",
    ageBand: relevantAttempts[0]?.ageBand ?? "5.0-5.5",
    attempts: allAttempts,
    relevantAttempts,
    lastRelevantAttempts,
    independentAttempts,
    countedMasteryAttempts,
    countedReadinessAttempts,
    transferAttempts,
    delayedReviewAttempts,
    startTime: sorted[0]?.timestamp ?? "",
    endTime: sorted[sorted.length - 1]?.timestamp ?? "",
    totalAttempts,
    accuracyRate,
    independentRate,
    highHintRate,
    h4Used,
    readAloudOnlyReadingEvidence,
    oralOnlyReadingEvidence,
    sameErrorLoop,
    dominantMisconceptionId,
    repeatedItemRisk,
    frustrationRate,
  };
}
